import { Grid } from '@mui/material';
import { Typography, useEventCallback } from '@mui/material';
import ResearchFilterMunicipalNumber from 'civil-status/components/municipal-number/research-filter-municipal-number';
import TableMunicipalNumber from 'civil-status/components/municipal-number/table';
import { ResearchMunicipalNumberCrumbs } from 'civil-status/constants/breadcrumb';
import { validationSchemaResearchMunicipalNumber } from 'civil-status/constants/schema-research-municipal-number';
import { transformerResearchMunicipalNumber } from 'civil-status/utils/transformer-research-municipal-number';
import { transformerResearchMunicipalNumberList } from 'civil-status/utils/transformer-research-municipal-number-list';
import {
  MunicipalNumberList,
  ResearchFilterMunicipalNumberVM,
} from 'civil-status/view-models/municipal-number';
import { Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import { Fragment, ReactElement, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { PmiButton } from 'shell/components/shared/button';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { HttpResponse } from 'shell/models/http-response';
import { MunicipalNumberResponse } from 'shell/models/municipal-numer';

const ResearchMunicipalNumberPage = (): ReactElement => {
  const params = useParams();
  const navigate = useNavigate();
  const { userProfile } = useUserProfile();

  const defaultInitialValues: ResearchFilterMunicipalNumberVM = useMemo(() => {
    return {
      register: params.idCat || '',
      nameRegister: params.valueCat?.toUpperCase() || '',
      dateFrom: DateTime.now(),
      dateTo: DateTime.now(),
      operator: userProfile.datiLogin?.key?.userName || '',
    };
  }, [params.idCat, params.valueCat, userProfile.datiLogin]);

  // filtri selezionati
  const [filterSelected, setFilterSelected] = useState<ResearchFilterMunicipalNumberVM>({});

  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const [list, getList] = useHttpFetch<
    HttpResponse<Array<MunicipalNumberResponse>>,
    Array<MunicipalNumberList>
  >(`${__ACT_API_URL__}/numerazione-comunale/ricerca`, {
    onBootstrap: false,
    initialData: [],
    options: {
      loading: false,
      errorCatching: true,
    },
    transformer: transformerResearchMunicipalNumberList,
  });

  const getListData = useEventCallback(
    (values: ResearchFilterMunicipalNumberVM, queryString: string) => {
      (
        getList({
          params: {},
          paramsSerializer: () => queryString,
        }) as unknown as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        // salvo i filtri dell'ultima ricerca
        setFilterSelected(values);
      });
    }
  );

  const onSubmit = useEventCallback((values: ResearchFilterMunicipalNumberVM) => {
    const queryString = transformerResearchMunicipalNumber(values);

    setTableProps({
      page: 0,
      rowsPerPage: 10,
    });
    getListData(values, queryString);
  });

  const goToRequest = useEventCallback(() => {
    if (!(params.idCat || params.valueCat)) {
      return;
    }

    navigate(
      generatePath('/stato-civile/:valueCat/:idCat/richiesta-numero', {
        idCat: params.idCat,
        valueCat: params.valueCat,
      })
    );
  });

  const itemsBreadcrubs: Array<BreadcrumbItem> = useMemo(() => {
    return ResearchMunicipalNumberCrumbs(params.valueCat || '', params.idCat || '');
  }, [params.valueCat, params.idCat]);

  return (
    <Fragment>
      <Breadcrumbs items={itemsBreadcrubs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Gestione numerazione comunale
        </Typography>
      </div>
      <div className="circle" />
      <Formik
        initialValues={defaultInitialValues}
        validateOnMount
        validationSchema={validationSchemaResearchMunicipalNumber}
        onSubmit={onSubmit}
      >
        <Form>
          <ResearchFilterMunicipalNumber />
        </Form>
      </Formik>
      {list.data ? (
        ((list.data as Array<MunicipalNumberList>) || [])?.length > 0 ? (
          <TableMunicipalNumber
            list={list.data as Array<MunicipalNumberList>}
            getListData={getListData}
            tableProps={tableProps}
            setTableProps={setTableProps}
            filterSelected={filterSelected}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'center' }}>
          <PmiButton variant="outlined" color="cdmBrandThird" fullWidth onClick={goToRequest}>
            Vai alla richiesta
          </PmiButton>
        </Grid>
      </Grid>
    </Fragment>
  );
};

ResearchMunicipalNumberPage.displayName = 'Research Municipal Number Page component';

export default ResearchMunicipalNumberPage;
